import { useEffect, useRef, useCallback, useState } from "react";

export type WSMessage =
  | { type: "challenge_received"; challengeId: number; challengerName: string; gameTitle: string; entryFee: number; timestamp: number }
  | { type: "challenge_accepted"; challengeId: number; acceptedByName: string; gameId: number; gameTitle: string; timestamp: number }
  | { type: "challenge_declined"; challengeId: number; declinedByName: string; gameTitle: string; timestamp: number }
  | { type: "challenge_cancelled"; challengeId: number; cancelledByName: string; gameTitle: string; timestamp: number }
  | { type: "challenge_expired"; challengeId: number; gameTitle: string; timestamp: number }
  | { type: "notification"; title: string; message: string; notificationType: string; timestamp: number }
  | { type: "connected"; userId: number; timestamp: number }
  | { type: "ping" };

interface UseWebSocketOptions {
  enabled?: boolean;
  onMessage?: (msg: WSMessage) => void;
}

export function useWebSocket({ enabled = true, onMessage }: UseWebSocketOptions = {}) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptRef = useRef(0);
  const [connected, setConnected] = useState(false);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const connect = useCallback(() => {
    if (!enabled) return;

    // Build WebSocket URL from current location
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = `${protocol}//${window.location.host}/ws`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        reconnectAttemptRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const msg: WSMessage = JSON.parse(event.data);

          // Respond to server pings
          if (msg.type === "ping") {
            ws.send(JSON.stringify({ type: "pong" }));
            return;
          }

          // Forward to handler
          onMessageRef.current?.(msg);
        } catch {
          // Ignore malformed messages
        }
      };

      ws.onclose = () => {
        setConnected(false);
        wsRef.current = null;

        // Reconnect with exponential backoff (max 30s)
        if (enabled) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptRef.current), 30000);
          reconnectAttemptRef.current++;
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        }
      };

      ws.onerror = () => {
        // onclose will fire after onerror
      };
    } catch {
      // Failed to create WebSocket, retry
      const delay = Math.min(1000 * Math.pow(2, reconnectAttemptRef.current), 30000);
      reconnectAttemptRef.current++;
      reconnectTimeoutRef.current = setTimeout(connect, delay);
    }
  }, [enabled]);

  useEffect(() => {
    if (enabled) {
      connect();
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect, enabled]);

  return { connected };
}
